import React, { FocusEvent } from 'react';
import lodashDelay from 'lodash/delay';
import queryString from 'query-string';
import Autocomplete, { AutocompleteItem } from '../autocomplete';
import autoCompleteTypes from './async-autocomplete-constants';
import { AsyncAutocompleteProps } from './async-autocomplete-types';

export const ASYNC_AUTOCOMPLETE_DEBOUNCE_DELAY = 500;

type Action<T> = { type: 'itemsRequest' | 'itemsSuccess' | 'itemsFailure' | 'clear'; items?: T[] };
type State<T> = { items?: T[]; isFetching?: boolean; hasFetched?: boolean };

const useDebouncedEffect = (callback: () => void, delay: number, deps?: any[]): void => {
  const firstUpdate = React.useRef(true);
  React.useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }
    const handler = lodashDelay(() => {
      callback();
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [delay, ...(deps || [])]);
};

const reducer = <T,>(state: State<T>, action: Action<T>): State<T> => {
  switch (action.type) {
    case 'itemsRequest': {
      return { ...state, items: [], isFetching: true, hasFetched: false };
    }
    case 'itemsSuccess': {
      const { items = [] } = action;
      return {
        ...state,
        items: Object.values(items || []),
        isFetching: false,
        hasFetched: true,
      };
    }
    case 'itemsFailure': {
      return { ...state, items: [], isFetching: false, hasFetched: true };
    }
    case 'clear': {
      return { ...state, items: [], isFetching: false, hasFetched: false };
    }
    default:
      throw new Error();
  }
};

const AsyncAutocomplete = <T = AutocompleteItem,>({
  apiAuthClient,
  items,
  initialItems,
  type,
  minChars = 1,
  autocompleteQuery,
  subQueries,
  shouldAvoidFetch,
  onChange: onChangeProp,
  onSelect: onSelectProp,
  onBlur: onBlurProp,
  formatItems,
  autoCompleteFetchOptions = {},
  url: urlProp,
  extractListFromResponse,
  queryKey = 'q',
  ...rest
}: AsyncAutocompleteProps<T>): React.JSX.Element => {
  const [state, dispatch] = React.useReducer(reducer<T>, {
    isFetching: false,
    items,
    hasFetched: false,
  });
  /**
   * localInputValue is here only for debouncing the fetch function in a convenient way
   * it is not displayed nor used to anything beside triggering the debounced effect
   */
  const [localTextValue, setLocalTextValue] = React.useState('');

  useDebouncedEffect(
    async () => {
      const value = localTextValue;
      const shouldAvoidFetchSuggestions = (minChars && `${value}`.length < minChars) || shouldAvoidFetch?.(value);
      if (typeof value === 'string' && !shouldAvoidFetchSuggestions) {
        const urlOfAutoCompleteType = type ? autoCompleteTypes[type]?.url : undefined;
        const autocompleteTypeDefaultQuery = type ? autoCompleteTypes[type]?.query : {};

        const query = {
          [queryKey]: value,
          ...autocompleteTypeDefaultQuery,
          ...autocompleteQuery,
          ...subQueries,
        };
        dispatch({ type: 'itemsRequest' });
        try {
          if (!apiAuthClient) {
            throw new Error('apiAuthClient is required');
          }

          const endPoint = urlOfAutoCompleteType || urlProp;
          const response = await apiAuthClient?.fetch?.(
            `${autoCompleteFetchOptions?.domain || ''}${endPoint}?${queryString.stringify(query)}`,
            autoCompleteFetchOptions?.apiClientOptions || {},
          );

          if (!response.ok) {
            throw new Error('Failed to perform autocomplete call');
          }

          const data = await response.json();
          const items = extractListFromResponse?.(data) || data;
          const finalItems = formatItems ? formatItems(items) : items;

          dispatch({ type: 'itemsSuccess', items: finalItems });
        } catch {
          dispatch({ type: 'itemsFailure' });
        }
      } else {
        dispatch({ type: 'clear' });
      }
    },
    ASYNC_AUTOCOMPLETE_DEBOUNCE_DELAY,
    [localTextValue],
  );

  const onChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement> | React.MouseEvent<HTMLElement>, value?: string) => {
      onChangeProp?.(e, value);
      setLocalTextValue(value || '');
    },
    [onChangeProp],
  );

  const onSelect = (item: T) => {
    onSelectProp?.(item);

    dispatch({ type: 'clear' });
  };

  const onBlur = (e?: FocusEvent<any> | undefined) => {
    onBlurProp?.(e);
  };

  // `initialItems` are shown only as a starting point — before the user has typed anything (and
  // thus before any fetch has resolved). The moment a query runs, the fetched `state.items` take
  // over (including an empty list when nothing matches), so the dropdown always reflects the search.
  // The legacy `items` prop keeps its original "always override" behavior for back-compat.
  const showInitialItems = initialItems && !state.hasFetched && localTextValue.length === 0;
  const resolvedItems = items || (showInitialItems ? initialItems : state.items);

  return (
    <Autocomplete<T>
      isFetching={state.isFetching}
      hasFetched={state.hasFetched}
      items={resolvedItems}
      onBlur={onBlur}
      onEnter={() => dispatch({ type: 'clear' })}
      onSelect={onSelect}
      onChange={onChange}
      {...rest}
    />
  );
};

export default AsyncAutocomplete;
